import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plus } from "lucide-react";
import xrayPoster from "@/assets/media/xray-scan-loop-poster.webp";
import xrayVideo from "@/assets/media/xray-scan-loop.mp4";

export default function Intermission() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  const mediaScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.18, 1.08]);

  useEffect(() => {
    const section = ref.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video && shouldLoadVideo) {
      video.load();
      video.play().catch(() => {});
    }
  }, [shouldLoadVideo]);

  return (
    <section ref={ref} data-testid="intermission-section" className="relative overflow-hidden py-32 lg:py-44">
      <motion.div
        style={{ y: mediaY, scale: mediaScale }}
        className="absolute -inset-y-[20%] inset-x-0 will-change-transform"
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={xrayPoster}
          aria-hidden
          tabIndex={-1}
          data-testid="intermission-image"
          className="h-full w-full object-cover object-[center_52%] brightness-110 contrast-125"
        >
          {shouldLoadVideo && <source src={xrayVideo} type="video/mp4" />}
        </video>
        <div className="absolute inset-0 bg-void/5" />
        <div className="absolute inset-0 bg-gradient-to-r from-void/10 via-transparent to-void/10" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center sm:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <span className="mx-auto mb-8 flex h-10 w-10 items-center justify-center border border-neon/70 bg-void/30">
            <Plus className="h-7 w-7 text-neon" strokeWidth={2.5} />
          </span>
          <p className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.35em] text-neon">
            02 // Intermission
          </p>
          <h2
            data-testid="intermission-headline"
            className="font-display text-2xl font-light uppercase leading-snug tracking-tight text-[#EAEAEA] sm:text-4xl lg:text-5xl"
          >
            Please prepare a hot cup of tea, bake your own cake, take a slice,{" "}
            <span className="text-neon">and enjoy it.</span>
          </h2>
        </motion.div>
      </div>
    </section>
  );
}
